import posthog from "posthog-js";

// Same PostHog project (US Cloud, project 305562) that the One-Now-Frontend
// admin "monthly-review" dashboard reads back via HogQL — so customer-site
// traffic shows up per-tenant there. The phc_… key is a publishable client key.
const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY ||
  "phc_g5RzAulwRdukonaaOvc9690wQBbCLCWeujVaIJtYtM2";
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

/**
 * The tracing below is deliberate — it is how we confirm, from a tenant's own
 * browser, that events are firing and carrying the right company_id. It has no
 * place in a renter-facing production build though, so it compiles out there:
 * CRA sets NODE_ENV=production for `yarn build` regardless of REACT_APP_ENV,
 * which makes this a constant the minifier can fold away.
 */
const debug = process.env.NODE_ENV !== "production";
const debugLog = (...args: any[]): void => {
  if (debug) console.log(...args);
};
const debugWarn = (...args: any[]): void => {
  if (debug) console.warn(...args);
};

export function initPostHog(): void {
  if (initialized || typeof window === "undefined") return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false, // we fire $pageview manually on route change
    capture_pageleave: true,
    loaded: () => {
      debugLog("[PostHog] initialized →", POSTHOG_HOST);
    },
  });
  // Exposed for quick console testing, e.g. simulating a traffic source:
  //   window.posthog.capture('$pageview', { $referring_domain: 'www.tiktok.com' })
  (window as any).posthog = posthog;
  initialized = true;
}

/**
 * Stamp the tenant's company_id onto every subsequent event as a PostHog
 * super-property. The admin monthly-review dashboard filters all HogQL by
 * `properties.company_id`, so this is what makes the data show "according to
 * company id". Mirrors posthog.register({ company_id }) in One-Now-Frontend's
 * use-posthog.ts on the posthog-analytics branch.
 */
export function setPostHogCompany(
  companyId: number | string | null | undefined
): void {
  if (
    !initialized ||
    companyId === null ||
    companyId === undefined ||
    companyId === ""
  ) {
    debugWarn("[PostHog] setPostHogCompany skipped", {
      initialized,
      companyId,
    });
    return;
  }
  posthog.register({ company_id: String(companyId) });
  debugLog(
    "[PostHog] company_id registered as super-property →",
    String(companyId)
  );
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  if (!initialized) {
    debugWarn("[PostHog] trackEvent skipped (not initialized):", eventName);
    return;
  }
  posthog.capture(eventName, properties);
  debugLog("[PostHog] event →", eventName, properties ?? {});
}

export function capturePageview(): void {
  if (!initialized) return;
  const url = window.location.href;
  posthog.capture("$pageview", { $current_url: url });
  debugLog("[PostHog] $pageview →", url);
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
  debugLog("[PostHog] reset");
}

// Init as a module-load side effect (mirrors fleet's posthog-provider.tsx).
// Not during the react-snap prerender this site runs after `yarn build`
// (Template has no prerender): it would bake PostHog's injected tags into
// the static HTML and record the build machine as a visitor.
if (typeof window !== "undefined" && navigator.userAgent !== "ReactSnap")
  initPostHog();

export { posthog };
